import { useCallback, useMemo, useRef, useState } from 'react';
import { Sentry } from '@idk-web/core-utils';
import { useIsMounted } from '@idk-web/core-ui';
import { InboxSignResponse } from '@idk-web/api';
import { isBankIDError } from '@/utils/error';
import * as inboxSign from '@/hooks/inbox/sign/inboxSign';
import { StartedInboxSignRequest } from '@/hooks/inbox/sign/inboxSign';

type State =
  | {
      state: 'IDLE';
    }
  | {
      state: 'CANCELLED';
    }
  | {
      state: 'TIMED_OUT';
    }
  | {
      state: 'WAITING';
      bankId?: {
        autoStartToken: string;
        qrData: string;
      };
      freja?: {
        qrData: string;
      };
    }
  | {
      state: 'COMPLETE';
    }
  | {
      state: 'FAILED';
      error: unknown;
    };

export type InboxSign = {
  signBankIdSe(messageId: string): Promise<void>;
  signFreja(messageId: string): Promise<void>;
  signBankIdNo(messageId: string, window: Window): Promise<void>;
  signMitId(messageId: string, window: Window): Promise<void>;
  signFtn(messageId: string, window: Window): Promise<void>;
  cancel(): Promise<void>;
  reset(): void;
} & State;

export function useInboxSign(): InboxSign {
  const isMounted = useIsMounted();
  const request = useRef<StartedInboxSignRequest>(undefined);
  const [state, setState] = useState<State>({ state: 'IDLE' });
  const onInit = useCallback(
    async (data: Extract<InboxSignResponse, { status: 'IN_PROGRESS' }>) =>
      onProgress(data),
    [],
  );
  const onComplete = useCallback(async () => {
    if (isMounted()) {
      setState({ state: 'COMPLETE' });
    }

    request.current = undefined;
  }, []);
  const onError = useCallback(async (error: unknown) => {
    if (isMounted()) {
      if (error === 'cancelled') {
        setState({ state: 'CANCELLED' });
      } else if (error === 'timed out') {
        setState({ state: 'TIMED_OUT' });
      } else {
        if (!isBankIDError(error)) {
          Sentry.reportError('Inbox sign failed', { error });
        }

        setState({ state: 'FAILED', error });
      }
    }

    request.current = undefined;
  }, []);
  const onProgress = useCallback(
    async (data: Extract<InboxSignResponse, { status: 'IN_PROGRESS' }>) => {
      setState((oldState) => {
        let newState: State;
        if (data.bankId) {
          newState = { state: 'WAITING', bankId: data.bankId };
        } else if (data.freja) {
          newState = { state: 'WAITING', freja: data.freja };
        } else {
          newState = { state: 'WAITING' };
        }

        if (JSON.stringify(oldState) === JSON.stringify(newState)) {
          return oldState;
        } else {
          return newState;
        }
      });
    },
    [],
  );
  const signBankIdSe = useCallback(
    async (messageId: string): Promise<void> => {
      if (request.current) {
        return;
      }

      setState({ state: 'WAITING' });

      request.current = inboxSign
        .bankIdSe(messageId)
        .onInit(onInit)
        .onComplete(onComplete)
        .onError(onError)
        .onProgress(onProgress)
        .start();

      return request.current.wait();
    },
    [onInit, onComplete, onError, onProgress],
  );
  const signFreja = useCallback(
    async (messageId: string): Promise<void> => {
      if (request.current) {
        return;
      }

      setState({ state: 'WAITING' });

      request.current = inboxSign
        .freja(messageId)
        .onInit(onInit)
        .onComplete(onComplete)
        .onError(onError)
        .onProgress(onProgress)
        .start();

      return request.current.wait();
    },
    [onInit, onComplete, onError, onProgress],
  );
  const signBankIdNo = useCallback(
    async (messageId: string, window: Window): Promise<void> => {
      if (request.current) {
        return;
      }

      setState({ state: 'WAITING' });

      request.current = inboxSign
        .bankIdNo(messageId, window)
        .onInit(onInit)
        .onComplete(onComplete)
        .onError(onError)
        .onProgress(onProgress)
        .start();

      return request.current.wait();
    },
    [onInit, onComplete, onError, onProgress],
  );
  const signMitId = useCallback(
    async (messageId: string, window: Window): Promise<void> => {
      if (request.current) {
        return;
      }

      setState({ state: 'WAITING' });

      request.current = inboxSign
        .mitId(messageId, window)
        .onInit(onInit)
        .onComplete(onComplete)
        .onError(onError)
        .onProgress(onProgress)
        .start();

      return request.current.wait();
    },
    [onInit, onComplete, onError, onProgress],
  );
  const signFtn = useCallback(
    async (messageId: string, window: Window): Promise<void> => {
      if (request.current) {
        return;
      }

      setState({ state: 'WAITING' });

      request.current = inboxSign
        .ftn(messageId, window)
        .onInit(onInit)
        .onComplete(onComplete)
        .onError(onError)
        .onProgress(onProgress)
        .start();

      return request.current.wait();
    },
    [onInit, onComplete, onError, onProgress],
  );
  const cancel = useCallback(async () => {
    if (isMounted()) {
      setState({ state: 'CANCELLED' });
    }

    return request.current?.cancel();
  }, []);
  const reset = useCallback(() => {
    if (isMounted()) {
      setState({ state: 'IDLE' });
    }

    request.current = undefined;
  }, []);

  return useMemo(
    () => ({
      signBankIdSe,
      signFreja,
      signBankIdNo,
      signMitId,
      signFtn,
      cancel,
      reset,
      ...state,
    }),
    [
      signBankIdSe,
      signFreja,
      signBankIdNo,
      signMitId,
      signFtn,
      cancel,
      reset,
      state,
    ],
  );
}
